import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type PerfilNome } from '../dados/api.js'
import { useUsuarioAtual } from '../dados/usuarioAtual.js'
import { rotuloPerfil } from './admin-comum.js'

/// Editor dos perfis de uma pessoa.
///
/// Os dois perfis de comando — supremo e administrador — só aparecem habilitados
/// para o supremo. O servidor recusa de qualquer forma; travar aqui evita o clique
/// que só produz erro, e deixa visível *por que* está travado.

export const COMUNS: PerfilNome[] = [
  'GERENTE_CONTRATO',
  'ENGENHEIRO_SEGURANCA',
  'TECNICO_SEGURANCA',
  'ENCARREGADO',
  'COLABORADOR',
  'RESPONSAVEL_NC',
]
export const COMANDO: PerfilNome[] = ['SUPERADMIN', 'ADMIN']

export const EXPLICACAO: Partial<Record<PerfilNome, string>> = {
  SUPERADMIN: 'Gerencia os administradores. Só ele dá e tira este perfil e o de administrador.',
  ADMIN: 'Gerencia usuários comuns, vê a auditoria, monta checklists e classifica a Criticidade Ápia.',
  ENGENHEIRO_SEGURANCA: 'Enxerga todas as obras.',
  GERENTE_CONTRATO: 'Enxerga todas as obras.',
  RESPONSAVEL_NC:
    'Aparece na lista de responsáveis das não conformidades das obras dela. Não dá acesso nem permissão, e o RM não mexe nele.',
}

export function PerfisUsuario({
  usuarioId,
  perfis,
  souEu,
}: {
  usuarioId: string
  perfis: PerfilNome[]
  souEu: boolean
}) {
  const cliente = useQueryClient()
  const [escolhidos, setEscolhidos] = useState<PerfilNome[]>(perfis)
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvo, setSalvo] = useState(false)

  // Do servidor, não da sessão guardada: um perfil concedido hoje precisa valer
  // hoje, e não daqui a 30 dias quando a pessoa entrar de novo.
  const { usuario: eu } = useUsuarioAtual()
  const souSupremo = eu?.superAdmin ?? false

  const salvar = useMutation({
    mutationFn: () => api.adminDefinirPerfis(usuarioId, escolhidos, motivo.trim() || undefined),
    onSuccess: () => {
      setErro(null)
      setSalvo(true)
      setMotivo('')
      void cliente.invalidateQueries({ queryKey: ['admin'] })
    },
    onError: (e) => setErro((e as Error).message),
  })

  function alternar(p: PerfilNome) {
    setSalvo(false)
    setEscolhidos((atual) =>
      atual.includes(p) ? atual.filter((x) => x !== p) : [...atual, p],
    )
  }

  const mudou =
    escolhidos.length !== perfis.length || escolhidos.some((p) => !perfis.includes(p))

  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Perfis</p>

      <div className="space-y-1">
        {[...COMANDO, ...COMUNS].map((p) => {
          const deComando = COMANDO.includes(p)
          const travado = deComando && !souSupremo
          return (
            <label
              key={p}
              className={`flex items-start gap-2 rounded p-1.5 ${
                travado ? 'opacity-60' : 'hover:bg-white'
              } ${deComando ? 'border-l-2 border-limao pl-2' : ''}`}
            >
              <input
                type="checkbox"
                checked={escolhidos.includes(p)}
                onChange={() => alternar(p)}
                disabled={travado || salvar.isPending}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800">{rotuloPerfil[p]}</span>
                {EXPLICACAO[p] && (
                  <span className="block text-xs text-slate-500">{EXPLICACAO[p]}</span>
                )}
              </span>
            </label>
          )
        })}
      </div>

      {!souSupremo && (
        <p className="mt-2 text-xs text-slate-500">
          Só o administrador supremo pode dar ou retirar os perfis destacados.
        </p>
      )}
      {souEu && souSupremo && (
        <p className="mt-2 text-xs text-slate-500">
          Você não pode retirar o próprio perfil de supremo — o sistema ficaria sem ninguém para
          gerir administradores.
        </p>
      )}

      {mudou && (
        <div className="mt-2 space-y-2">
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivo (fica no registro)"
            className="campo"
          />
          <div className="flex gap-2">
            <button
              onClick={() => salvar.mutate()}
              disabled={salvar.isPending}
              className="botao-primario !min-h-0 flex-1 !py-1.5 text-sm"
            >
              {salvar.isPending ? 'Salvando…' : 'Salvar perfis'}
            </button>
            <button
              onClick={() => {
                setEscolhidos(perfis)
                setMotivo('')
                setErro(null)
              }}
              className="botao-neutro !min-h-0 !py-1.5 text-sm"
            >
              Desfazer
            </button>
          </div>
        </div>
      )}

      {salvo && !mudou && (
        <p className="mt-2 text-sm text-leve" role="status">
          Perfis salvos.
        </p>
      )}
      {erro && (
        <p className="mt-2 rounded bg-alta/10 px-3 py-2 text-sm text-alta" role="alert">
          {erro}
        </p>
      )}
    </div>
  )
}
